import React, { useState, useEffect } from 'react';
import { Sidebar } from './components/Sidebar';
import { Header } from './components/Header';
import { ContentDashboard } from './components/ContentDashboard';
import { AuthScreen } from './components/AuthScreen';
import { UserProfileModal } from './components/UserProfileModal'; // Import Modal
import { PricingModal } from './components/PricingModal'; // Import Pricing Modal
import { SubscriptionManagerModal } from './components/SubscriptionManagerModal'; // Import Subscription Modal
import { LandingPage } from './components/LandingPage';
import { analyzeTranscript } from './services/geminiService';
import { supabase, isSupabaseConfigured } from './services/supabaseClient';
import { useAuth } from './context/AuthContext';
import { AppState, ViewMode, HistoryItem, UserProfile, Subscription, MonthlyUsage } from './types';
import { Youtube, AlertCircle, ArrowRight, Clock, Star, ChevronRight, Copy, Loader2, Search } from 'lucide-react';

export default function App() {
  const { user, loading: authLoading } = useAuth();

  const [state, setState] = useState<AppState>({
    transcript: '',
    isLoading: false,
    result: null,
    currentId: null,
    error: null,
  });

  const [view, setView] = useState<ViewMode>(ViewMode.INPUT);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [isOffline, setIsOffline] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Profile State
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [monthlyUsage, setMonthlyUsage] = useState<MonthlyUsage | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);

  // Auth/Landing State
  const [authView, setAuthView] = useState<'landing' | 'auth'>('landing');
  const [authIntent, setAuthIntent] = useState<'free' | 'pro_monthly' | 'pro_annual' | undefined>(undefined);
  const [isCheckoutRedirecting, setIsCheckoutRedirecting] = useState(false);

  // Handle Checkout Redirect globally
  useEffect(() => {
    const handleCheckoutRedirect = async () => {
      if (user && (authIntent === 'pro_monthly' || authIntent === 'pro_annual')) {
        setIsCheckoutRedirecting(true);
        // @ts-ignore
        const priceId = authIntent === 'pro_annual' ? import.meta.env.VITE_STRIPE_PRO_ANNUAL_PRICE_ID : import.meta.env.VITE_STRIPE_PRO_MONTHLY_PRICE_ID;

        try {
          const { data: sessionData, error } = await supabase.functions.invoke('create-checkout-session', {
            body: { price_id: priceId }
          });

          if (sessionData?.url) {
            window.location.href = sessionData.url;
            return; // Wait for redirect
          } else {
            console.error('Falha ao criar sessão do Stripe:', error || sessionData);
            setIsCheckoutRedirecting(false);
            setAuthIntent(undefined); // clean up intent
          }
        } catch (e) {
          console.error('Erro ao redirecionar para checkout', e);
          setIsCheckoutRedirecting(false);
          setAuthIntent(undefined);
        }
      } else if (user && authIntent === 'free') {
        setAuthIntent(undefined); // Clear intent
      }
    };

    handleCheckoutRedirect();
  }, [user, authIntent]);

  // Fetch Profile Data
  const fetchProfile = async () => {
    if (!user || !isSupabaseConfigured) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (data) {
        setUserProfile(data);
      }

      // Fetch subscription with plan details
      const { data: subData } = await supabase
        .from('subscriptions')
        .select('*, plan:plans(*)')
        .eq('user_id', user.id)
        .single();

      if (subData) {
        setSubscription(subData as unknown as Subscription);
      }

      // Fetch current month usage
      const currentDate = new Date();
      const currentCycle = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;

      const { data: usageData, error: usageError } = await supabase
        .from('monthly_usage')
        .select('*')
        .eq('user_id', user.id)
        .eq('billing_cycle', currentCycle)
        .single();

      if (usageData) {
        setMonthlyUsage(usageData);
      } else {
        // Padrão caso ainda não exista registro no mês
        setMonthlyUsage({
          id: '',
          user_id: user.id,
          billing_cycle: currentCycle,
          projects_count: 0
        });
      }

      return subData as unknown as Subscription;
    } catch (error) {
      console.error("Error fetching profile and subscription:", error);
      return null;
    }
  };

  // Fetch History from Supabase when User changes
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setHistory([]);
      setUserProfile(null);
      return;
    }

    const initData = async () => {
      // Se não estiver configurado, apenas marca erro e retorna (não usa local storage)
      if (!isSupabaseConfigured) {
        setIsOffline(true);
        return;
      }

      const currentSub = await fetchProfile(); // Get profile data and subscription

      try {
        // Produção: Filtro explícito + RLS no backend
        let query = supabase
          .from('projects')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        // Limit history for Free users
        if (currentSub?.plan && currentSub.plan.history_retention_days !== -1) {
          const limitDate = new Date();
          limitDate.setDate(limitDate.getDate() - currentSub.plan.history_retention_days);
          query = query.gte('created_at', limitDate.toISOString());
        }

        const { data, error } = await query;

        if (error) {
          console.error("Supabase Error:", error.message);
          setIsOffline(true);
          return;
        }

        if (data) {
          setIsOffline(false);
          const formattedHistory: HistoryItem[] = data.map((item: any) => ({
            id: item.id,
            date: new Date(item.created_at).getTime(),
            transcript: item.transcript,
            result: item.result,
            isFavorite: item.is_favorite
          }));
          setHistory(formattedHistory);
        }
      } catch (err: any) {
        console.error("Connection error:", err.message);
        setIsOffline(true);
      }
    };

    initData();
  }, [user, authLoading]);

  const handleProcess = async () => {
    if (!state.transcript.trim()) return;
    if (!user) {
      alert("Você precisa estar logado para gerar insights.");
      return;
    }

    // Checking Quota
    if (subscription?.plan) {
      const maxProjects = subscription.plan.max_projects_per_month;
      if (maxProjects !== -1 && monthlyUsage && monthlyUsage.projects_count >= maxProjects) {
        setState((prev) => ({
          ...prev,
          error: `Você atingiu o limite mensal de ${maxProjects} projetos do Plano ${subscription.plan.name}. Faça upgrade para continuar.`
        }));
        setIsPricingModalOpen(true);
        return;
      }
    }

    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      // 1. Generate Content via Gemini
      const result = await analyzeTranscript(state.transcript);

      // 2. Save Data directly to Supabase
      if (!isSupabaseConfigured) {
        throw new Error("Banco de dados não configurado.");
      }

      const { data, error: dbError } = await supabase
        .from('projects')
        .insert({
          transcript: state.transcript,
          result: result,
          is_favorite: false,
          user_id: user.id
        })
        .select()
        .single();

      if (dbError) {
        throw new Error("Erro ao salvar no banco: " + dbError.message);
      }

      if (data) {
        // UPDATE USAGE
        const currentDate = new Date();
        const currentCycle = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
        const newCount = (monthlyUsage?.projects_count || 0) + 1;

        try {
          if (monthlyUsage?.id) {
            await supabase.from('monthly_usage').update({ projects_count: newCount }).eq('id', monthlyUsage.id);
            setMonthlyUsage(prev => prev ? { ...prev, projects_count: newCount } : null);
          } else {
            const { data: insertedUsage } = await supabase.from('monthly_usage').insert({
              user_id: user.id,
              billing_cycle: currentCycle,
              projects_count: newCount
            }).select().single();
            if (insertedUsage) {
              setMonthlyUsage(insertedUsage as unknown as MonthlyUsage);
            }
          }
        } catch (usageError) {
          console.error("Erro ao atualizar uso mensal:", usageError);
        }

        const newItem: HistoryItem = {
          id: data.id,
          date: new Date(data.created_at).getTime(),
          transcript: data.transcript,
          result: data.result,
          isFavorite: data.is_favorite
        };

        setHistory((prev) => [newItem, ...prev]);
        setState((prev) => ({
          ...prev,
          result,
          isLoading: false,
          currentId: data.id
        }));
        setView(ViewMode.DASHBOARD);
        setSearchTerm('');
      }

    } catch (err: any) {
      console.error(err);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: err.message || "Falha ao processar e salvar"
      }));
    }
  };

  const handleNewProject = () => {
    setView(ViewMode.INPUT);
    setState(prev => ({ ...prev, transcript: '', result: null, currentId: null, error: null }));
    setSearchTerm('');
  };

  const handleToggleFavorite = async () => {
    if (!state.currentId) return;

    const itemIndex = history.findIndex(h => h.id === state.currentId);
    if (itemIndex === -1) return;

    const currentItem = history[itemIndex];
    const newStatus = !currentItem.isFavorite;

    // Checar limite de Favoritos
    if (newStatus && subscription?.plan) {
      const maxFavorites = subscription.plan.max_favorites;
      if (maxFavorites !== -1) {
        const currentFavoritesCount = history.filter(h => h.isFavorite).length;
        if (currentFavoritesCount >= maxFavorites) {
          setIsPricingModalOpen(true);
          return;
        }
      }
    }

    // Optimistic Update
    const updatedHistory = [...history];
    updatedHistory[itemIndex] = { ...currentItem, isFavorite: newStatus };
    setHistory(updatedHistory);

    if (isSupabaseConfigured) {
      const { error } = await supabase
        .from('projects')
        .update({ is_favorite: newStatus })
        .eq('id', state.currentId)
        .eq('user_id', user?.id); // Segurança extra na atualização

      if (error) {
        console.error("Error updating favorite:", error);
        // Revert on error
        updatedHistory[itemIndex] = { ...currentItem, isFavorite: !newStatus };
        setHistory(updatedHistory);
        setState(prev => ({ ...prev, error: "Falha ao atualizar favorito no banco" }));
      }
    }
  };

  const restoreHistoryItem = (item: HistoryItem) => {
    setState(prev => ({
      ...prev,
      transcript: item.transcript,
      result: item.result,
      currentId: item.id,
      error: null
    }));
    setView(ViewMode.DASHBOARD);
    setSearchTerm(''); // Limpa a busca ao selecionar
  };

  const getCurrentIsFavorite = () => {
    if (!state.currentId) return false;
    const item = history.find(h => h.id === state.currentId);
    return item ? item.isFavorite : false;
  };

  const renderHistoryList = (items: HistoryItem[], emptyMessage: string) => {
    if (items.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-slate-400">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
            {searchTerm ? <Search size={32} className="opacity-50" /> : <Clock size={32} className="opacity-50" />}
          </div>
          <p>{emptyMessage}</p>
          {!searchTerm && (
            <button onClick={handleNewProject} className="mt-4 text-blue-600 font-medium hover:underline">
              Criar novo projeto
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {items.map(item => (
          <div
            key={item.id}
            onClick={() => restoreHistoryItem(item)}
            className="group bg-white p-6 rounded-[24px] border border-slate-100 hover:border-blue-200 shadow-sm hover:shadow-md transition-all cursor-pointer relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-4">
              {item.isFavorite && <Star size={16} className="text-yellow-400 fill-yellow-400" />}
            </div>

            <div className="text-xs font-bold text-slate-400 mb-2 uppercase tracking-wide">
              {new Date(item.date).toLocaleDateString('pt-BR')} • {new Date(item.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </div>

            <h3 className="font-semibold text-slate-800 mb-3 line-clamp-2 min-h-[3rem]">
              {item.result?.summary?.[0]?.point || "Sem resumo disponível"}
            </h3>

            <div className="flex items-center gap-2 mt-4 text-sm text-slate-500 group-hover:text-blue-600 transition-colors">
              <span>Ver análise</span>
              <ChevronRight size={16} />
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FB]">
        <Loader2 className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  if (isCheckoutRedirecting) {
    return (
      <div className="min-h-screen bg-[#F8F9FB] flex flex-col items-center justify-center p-4 text-center">
        <Loader2 className="animate-spin text-blue-600 mb-4 mx-auto" size={48} />
        <h2 className="text-2xl font-bold text-slate-900">Redirecionando para o pagamento seguro...</h2>
        <p className="text-slate-500 mt-2">Aguarde, você será levado para o Checkout da Stripe.</p>
      </div>
    );
  }

  if (!user) {
    if (authView === 'landing') {
      return (
        <LandingPage
          onLogin={() => {
            setAuthIntent(undefined);
            setAuthView('auth');
          }}
          onSignup={(intent) => {
            setAuthIntent(intent);
            setAuthView('auth');
          }}
        />
      );
    }
    return <AuthScreen intent={authIntent} />;
  }

  // Logic to determine what to render based on Search or ViewMode
  const renderMainContent = () => {
    // 1. Search Mode (Priority)
    if (searchTerm) {
      const filtered = history.filter(h =>
        h.transcript.toLowerCase().includes(searchTerm.toLowerCase()) ||
        h.result?.summary?.some(s => s.point.toLowerCase().includes(searchTerm.toLowerCase()))
      );

      return (
        <div className="py-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">Resultados da Busca</h2>
          {renderHistoryList(filtered, `Nenhum resultado encontrado para "${searchTerm}"`)}
        </div>
      );
    }

    // 2. Normal View Modes
    if (view === ViewMode.INPUT) {
      return (
        <div className="h-[calc(100vh-140px)] flex flex-col md:flex-row gap-8">
          {/* Left Panel: Context */}
          <div className="flex-1 bg-blue-50/50 border border-blue-100 rounded-[32px] p-8 md:p-12 flex flex-col justify-center items-start shadow-sm">
            <div className="w-16 h-16 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-8 text-red-600">
              <Youtube size={32} />
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4 leading-tight">
              Transforme Vídeo <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Em Estratégia</span>
            </h2>
            <p className="text-slate-500 text-lg mb-8 max-w-md">
              Cole a transcrição do seu vídeo. Vamos extrair insights acionáveis, criar um resumo e escrever seu conteúdo social instantaneamente.
            </p>

            <div className="flex -space-x-3 mb-6">
              {[1, 2, 3, 4].map(i => (
                <img key={i} src={`https://picsum.photos/40?random=${i}`} className="w-10 h-10 rounded-full border-2 border-white" alt="User" />
              ))}
              <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs border-2 border-white">+2k</div>
            </div>
            <p className="text-sm text-slate-400 font-medium">Confiado por criadores do mundo todo</p>
          </div>

          {/* Right Panel: Input Area */}
          <div className="flex-[1.5] bg-white rounded-[32px] border border-slate-100 shadow-lg shadow-slate-200/50 p-2 flex flex-col relative overflow-hidden">
            <textarea
              className="flex-1 w-full p-8 resize-none focus:outline-none text-slate-700 text-lg placeholder:text-slate-300 bg-transparent"
              placeholder="Cole a transcrição completa aqui..."
              value={state.transcript}
              onChange={(e) => setState(prev => ({ ...prev, transcript: e.target.value }))}
            />

            <div className="p-4 bg-white/80 backdrop-blur-sm absolute bottom-0 left-0 right-0 flex justify-between items-center border-t border-slate-50">
              <span className="text-xs text-slate-400 font-medium ml-4">
                {state.transcript.length > 0 ? `${state.transcript.split(' ').length} palavras` : 'Pronto para iniciar'}
              </span>
              <button
                onClick={handleProcess}
                disabled={state.isLoading || !state.transcript}
                className={`
                                group flex items-center gap-2 px-8 py-4 rounded-full font-bold text-white transition-all
                                ${state.isLoading || !state.transcript ? 'bg-slate-200 cursor-not-allowed text-slate-400' : 'bg-slate-900 hover:bg-slate-800 hover:scale-105 shadow-lg shadow-slate-900/20'}
                            `}
              >
                {state.isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Processando...
                  </>
                ) : (
                  <>
                    Gerar Estratégia
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (view === ViewMode.DASHBOARD && state.result) {
      return (
        <div className="h-[calc(100vh-140px)] flex gap-8 animate-in fade-in zoom-in duration-500 w-full">
          <ContentDashboard data={state.result} />
        </div>
      );
    }

    if (view === ViewMode.HISTORY) {
      return (
        <div className="py-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">Histórico de Análises {isOffline && <span className="text-sm font-normal text-red-600 bg-red-50 px-2 py-1 rounded-md ml-2 border border-red-100">Erro de Conexão</span>}</h2>
          {renderHistoryList(history, "Nenhuma análise no histórico.")}
        </div>
      );
    }

    if (view === ViewMode.FAVORITES) {
      return (
        <div className="py-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">Seus Projetos Favoritos</h2>
          {renderHistoryList(history.filter(i => i.isFavorite), "Você ainda não favoritou nenhum projeto.")}
        </div>
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB] text-slate-900 font-sans selection:bg-blue-100 pl-0 lg:pl-28">
      <Sidebar
        onNewProject={handleNewProject}
        onToggleFavorite={handleToggleFavorite}
        isFavorite={getCurrentIsFavorite()}
        userProfile={userProfile}
        onOpenProfile={() => setIsProfileModalOpen(true)}
        onOpenSubscription={() => setIsSubscriptionModalOpen(true)}
        onOpenPricing={() => setIsPricingModalOpen(true)}
        planName={subscription?.plan?.name}
      />

      {/* User Profile Modal */}
      <UserProfileModal
        user={user}
        profile={userProfile}
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        onProfileUpdate={fetchProfile}
      />

      {/* Subscription Manager Modal */}
      <SubscriptionManagerModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
        subscription={subscription}
        monthlyUsage={monthlyUsage}
        onReactivatePlan={() => {
          setIsSubscriptionModalOpen(false);
          setIsPricingModalOpen(true);
        }}
      />

      {/* Pricing Modal */}
      <PricingModal
        isOpen={isPricingModalOpen}
        onClose={() => setIsPricingModalOpen(false)}
      />

      <main className="max-w-[1600px] mx-auto min-h-screen flex flex-col">
        <Header
          currentView={view}
          onViewChange={(v) => {
            if (v === ViewMode.INPUT && state.result) {
              setView(ViewMode.DASHBOARD);
            } else if (v === ViewMode.INPUT) {
              setView(ViewMode.INPUT);
            } else {
              setView(v);
            }
          }}
          onSearch={setSearchTerm}
          searchTerm={searchTerm}
        />

        <div className="flex-1 p-4 md:p-8 pt-0">
          {/* Error Banner */}
          {state.error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-600 rounded-2xl flex items-center gap-3">
              <AlertCircle size={20} />
              <span>{state.error}</span>
            </div>
          )}

          {/* Dynamic Content Area (Search overrides View) */}
          {renderMainContent()}
        </div>
      </main>
    </div>
  );
}